import subjectsService from '@/api/subject-service';
import { removeArrayItem, updateArrayItem } from '@/util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultFormItem = () => ({
  is_visible: true,
  is_searchable_as_accompanying_user: false,
});

export const initialSubjectsFilterParams = () => ({});

const state = {
  subjects: null,
  subjectsPagination: {},
  newSubject: getDefaultFormItem(),
  editedSubject: {},
  subjectValidationErrors: {},
  subjectsFilterParams: initialSubjectsFilterParams(),
};

const mutations = {
  SET_SUBJECTS(state, { data, current_page, per_page, total }) {
    state.subjects = data;
    state.subjectsPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.subjectsFilterParams = params;
  },

  STORE_SUBJECT(state, subject) {
    state.subjects = [...state.subjects, subject];
    state.newSubject = getDefaultFormItem();
    state.subjectValidationErrors = {};
    state.subjectsPagination.total += 1;
  },

  SET_EDITED_SUBJECT(state, subject) {
    state.subjectValidationErrors = {};
    state.editedSubject = JSON.parse(JSON.stringify(subject));
  },

  UPDATE_SUBJECT(state, subject) {
    state.subjects = updateArrayItem(state.subjects, subject);
  },

  DELETE_SUBJECT(state, subject) {
    state.subjects = removeArrayItem(state.subjects, subject);
    state.subjectsPagination.total -= 1;
  },

  SET_SUBJECT_VALIDATION_ERRORS(state, validationErrors) {
    state.subjectValidationErrors = validationErrors;
  },

  RESET_SUBJECT_VALIDATION_ERRORS(state) {
    state.subjectValidationErrors = {};
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.subjectValidationErrors[field];
  },
};

const actions = {
  fetchSubjects({ state, commit }, params) {
    commit('SET_FILTER_PARAMS', params);
    return subjectsService.getPage(params).then((res) => {
      commit('SET_SUBJECTS', res.data);
      if (params.page !== state.subjectsFilterParams.page) {
        window.scrollTo(0, 0);
      }
    });
  },

  storeSubject({ commit }, subject) {
    return subjectsService
      .create(subject)
      .then((res) => {
        commit('STORE_SUBJECT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('subject_created'));
        return res;
      })
      .catch((err) => {
        commit('SET_SUBJECT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editSubject({ state, commit }, subjectId) {
    const subject = state.subjects?.find((s) => s.id === subjectId);
    if (subject) {
      commit('SET_EDITED_SUBJECT', subject);
      return Promise.resolve(subject);
    }
    return subjectsService.getById(subjectId).then((res) => {
      commit('SET_EDITED_SUBJECT', res.data);
      return res.data;
    });
  },

  updateSubject({ commit }, subject) {
    return subjectsService
      .update(subject)
      .then((res) => {
        commit('UPDATE_SUBJECT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('subject_updated'));
      })
      .catch((err) => {
        commit('SET_SUBJECT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteSubject({ commit }, subject) {
    openConfirmDialog({
      title: i18n.t('confirm_subject_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      subjectsService.delete(subject).then(() => {
        commit('DELETE_SUBJECT', subject);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('subject_deleted'));
      });
    });
  },

  restoreSubject({ commit }, subject) {
    openConfirmDialog({
      title: i18n.t('confirm_subject_restore'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      subjectsService.restore(subject)
      .then((res) => {
        commit('DELETE_SUBJECT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('subject_restored'));
      })
    });
  },

  clearSubjectValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
